import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function RefundPolicyPage() {
  const lastUpdated = "August 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-9 h-9 rounded-md flex items-center justify-center overflow-hidden">
                <Logo className="w-9 h-9" alt="" />
              </div>
              <span className="font-medium text-lg">No Half Days</span>
            </Link>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="page-title mb-2">Refund policy</h1>
          <p className="text-muted-foreground text-lg mb-8">Last updated: {lastUpdated}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Our Policy</h2>
            <div className="p-4 bg-ochre-soft rounded-cards mb-6">
              <p className="text-foreground font-medium">
                All subscription payments are final and non-refundable.
              </p>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              At No Half Days, operated by Your Figment Pty Ltd, we maintain a strict no-refund policy for all subscription payments. This policy applies to monthly subscription billing.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Why We Don't Offer Refunds</h2>
            <p className="text-muted-foreground leading-relaxed">
              We provide a generous <strong>30-day free trial</strong> with full access to all features. This trial period is designed to give you ample time to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Explore all features and functionality</li>
              <li>Create sample jobs and estimates</li>
              <li>Track your equipment and calculate ROI</li>
              <li>Evaluate whether the Service meets your needs</li>
              <li>Make an informed decision before committing to a paid subscription</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We strongly encourage you to use the full trial period before subscribing. If No Half Days isn't the right fit for you, simply cancel before your trial ends—no payment will be charged.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Cancellation</h2>
            <p className="text-muted-foreground leading-relaxed">
              You may cancel your subscription at any time through your account Settings page. When you cancel:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Your subscription will remain active until the end of your current billing period</li>
              <li>You will retain full access to all features until that date</li>
              <li>No further charges will be made after cancellation</li>
              <li>No partial or prorated refunds will be issued for the remaining time in your billing period</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Payment Processing</h2>
            <p className="text-muted-foreground leading-relaxed">
              All payments are processed through <strong>Polar</strong> (polar.sh), our Merchant of Record. Polar handles:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Payment processing and billing</li>
              <li>Invoice generation</li>
              <li>Tax calculation and compliance</li>
              <li>Payment disputes and chargebacks</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              If you have questions about a specific charge or payment, you may contact Polar directly or reach out to us and we will assist in resolving the issue.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Exceptional Circumstances</h2>
            <p className="text-muted-foreground leading-relaxed">
              While we maintain a strict no-refund policy, we understand that exceptional circumstances may arise. In rare cases, we may consider refund requests for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>Duplicate charges due to technical errors</li>
              <li>Charges made after a cancellation was processed</li>
              <li>Unauthorized transactions (subject to verification)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              All exceptional circumstance requests are reviewed on a case-by-case basis at our sole discretion. To request a review, please contact us with your account details and a description of the issue.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Failed Payments</h2>
            <p className="text-muted-foreground leading-relaxed">
              If a payment fails:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li>We will attempt to process the payment again</li>
              <li>You will receive email notifications about the failed payment</li>
              <li>You can update your payment method through the billing portal</li>
              <li>If payment cannot be collected, your subscription may be paused or cancelled</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Refund Policy or need assistance with your subscription, please contact us:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-foreground font-medium">Your Figment Pty Ltd</p>
              <p className="text-muted-foreground">Email: crew@nohalfdays.com</p>
              <p className="text-muted-foreground">Website: nohalfdays.com</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Related Policies</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Refund Policy should be read together with our:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
              <li><Link to="/terms" className="text-primary hover:underline">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link></li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Your Figment Pty Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
